import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import Breakpoints from 'lib/Breakpoints';
import { package as PackagePropType } from 'lib/CustomPropTypes';
import { isVideoLive } from 'lib/videoUtils';

import SlideshowTeasePicture from 'components/SlideshowTeasePicture';
import TeaseCard from 'components/TeaseCard';
import TeasePicture from 'components/TeasePicture';
import VideoPlayer from 'components/VideoPlayer';

import Playmaker from 'components/packages/Playmaker';
import getPlaymakerMetadata from 'components/packages/Playmaker/utils/getMetadata';

import teaseStyles from './teaseStyles.module.scss';

const getPlaymakerId = (metadata) => getPlaymakerMetadata(metadata).playmakerId;

class StraightUp extends React.Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    isRailAdjacent: PropTypes.bool,
    pkgClassName: PropTypes.string,
  };

  static childContextTypes = {
    setStickyVideoFlag: PropTypes.func,
  };

  static defaultProps = {
    isRailAdjacent: false,
    pkgClassName: 'pkg straight-up',
  };

  constructor(props) {
    super(props);
    const { content: { metadata } } = props;

    this.state = {
      isLive: false,
      hasStickyVideo: false,
      hasClickedToPlay: false,
      playmakerId: getPlaymakerId(metadata),
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { content: { metadata } } = props;
    const { playmakerId: prevPlaymakerId } = state;
    const playmakerId = getPlaymakerId(metadata);

    // Reset state if changed to/from Playmaker implementation or PID changed
    if (prevPlaymakerId !== playmakerId) {
      return {
        isLive: false,
        hasStickyVideo: false,
        hasClickedToPlay: false,
        playmakerId,
      };
    }
    return null;
  }

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  };

  getChildContext = () => ({
    setStickyVideoFlag: this.setStickyVideoFlag,
  });

  getPicture = () => {
    const { content: { items: [item] } } = this.props;
    const { computedValues, type } = item;

    if (type === 'slideshow') {
      // slides is now items[0].item.slides -- make sure this is correct
      const { item: { slides } } = item;

      return (
        <SlideshowTeasePicture
          slides={slides}
          sizes={{
            s: 360, m: 460, l: 260, xl: 380,
          }}
          rectangle
        />
      );
    }
    return (
      <TeasePicture
        responsiveFlavors={{
          s: AIMS_FLAVORS.FOCAL_758x379,
          m: AIMS_FLAVORS.FOCAL_1000x500,
        }}
        computedValues={computedValues}
        type={type}
        preview={{
          playsInline: true,
          redirect: true,
          url: computedValues.url,
        }}
      />
    );
  };

  getVideo = () => {
    const { hasClickedToPlay, hasStickyVideo } = this.state;
    const { content: { items: [item] } } = this.props;

    if (item.type !== 'video' || !hasClickedToPlay) {
      return null;
    }

    return (
      <div className={classNames('straight-up__video-container', { 'straight-up__video-container--is-stuck': hasStickyVideo })}>
        <div className="straight-up__video-resize">
          <VideoPlayer
            video={item.item}
            autoPlay
            manualPlay
          />
        </div>
      </div>
    );
  };

  getTease = () => {
    const { content: { metadata: { playmakerToggle } = {} } } = this.props;
    if (playmakerToggle === true) {
      return this.getPlaymakerTease();
    }
    return this.getNormalTease();
  };

  getNormalTease = () => {
    const {
      content: {
        items,
        metadata: packageMetadata,
      },
      isRailAdjacent,
      pkgClassName,
    } = this.props;

    const [item] = items;

    if (this.isLive()) {
      packageMetadata.showAuthor = true;
      packageMetadata.showTimestamp = true;
    }

    return item && (
      <React.Fragment>
        <TeaseCard
          content={item}
          imageClick={this.handleClick}
          isRailAdjacent={isRailAdjacent}
          pkgClassName={pkgClassName}
          showDek
          showFeaturedAuthor
          showLive={this.isLive()}
          styles={teaseStyles}
          {...item.metadata}
          {...packageMetadata}
        >
          {this.getPicture()}
        </TeaseCard>
        {this.getVideo()}
      </React.Fragment>
    );
  };

  getPlaymakerTease = () => {
    const { content, pkgClassName } = this.props;
    return (
      <Playmaker
        content={content}
        handleStatus={this.handleStatus}
        styleOverride={teaseStyles}
        pkgClassName={pkgClassName}
      />
    );
  };

  handleClick = (e) => {
    const { content: { items: [item] } } = this.props;

    if (Breakpoints.isSorM()) {
      return;
    }


    if (item.type === 'video') {
      e.preventDefault();
      this.setState({ hasClickedToPlay: true });
    }
  };

  handleStatus = (status, isLive) => {
    this.setState({ isLive });
  };

  isLive = () => {
    const { isLive } = this.state;
    if (isLive) {
      return true;
    }
    const { content: { items: [item] } } = this.props;
    return isVideoLive(item);
  };

  render() {
    const { pkgClassName, content: { id } } = this.props;

    return (
      <section
        data-packageid={id}
        className={classNames(pkgClassName)}
      >
        <div className={classNames('straight-up__inner', { 'straight-up__live': this.isLive() })}>
          {this.getTease()}
        </div>
      </section>
    );
  }
}

export default StraightUp;
